import "@fontsource/manrope/latin-400.css";
import "@fontsource/manrope/latin-ext-400.css";
import "@fontsource/manrope/latin-500.css";
import "@fontsource/manrope/latin-ext-500.css";
import "@fontsource/manrope/latin-600.css";
import "@fontsource/manrope/latin-ext-600.css";
import "@fontsource/manrope/latin-700.css";
import "@fontsource/manrope/latin-ext-700.css";
import "@fontsource/manrope/latin-800.css";
import "@fontsource/manrope/latin-ext-800.css";
import "@fontsource/fraunces/latin-400.css";
import "@fontsource/fraunces/latin-ext-400.css";
import "@fontsource/fraunces/latin-600.css";
import "@fontsource/fraunces/latin-ext-600.css";
import "@fontsource/fraunces/latin-600-italic.css";
import "@fontsource/fraunces/latin-ext-600-italic.css";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  stops,
  missions,
  recipes,
} from "./data.js";
import "./styles.css";

gsap.registerPlugin(ScrollTrigger);
document.documentElement.classList.add("js");

const iconPaths = {
  arrow:
    '<path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>',
  compass:
    '<circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.7"/><path d="m15.5 8.5-2.1 4.9-4.9 2.1 2.1-4.9 4.9-2.1Z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/>',
  route:
    '<path d="M5 18c0-3.5 5-3.5 5-7s-4-3.4-4-6c0-1.7 1.3-3 3-3s3 1.3 3 3c0 2.6-4 2.5-4 6s5 3.5 5 7" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/><circle cx="13" cy="19" r="3" stroke="currentColor" stroke-width="1.7"/>',
  map:
    '<path d="m3 6 5-3 8 3 5-3v15l-5 3-8-3-5 3V6Z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/><path d="M8 3v15M16 6v15" stroke="currentColor" stroke-width="1.7"/>',
  kitchen:
    '<path d="M8 3v8M5 3v5a3 3 0 0 0 6 0V3M8 11v10M16 3v18M16 3c3 2 4 5 4 8h-4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>',
  passport:
    '<rect x="5" y="3" width="14" height="18" rx="2" stroke="currentColor" stroke-width="1.7"/><path d="M9 8h6M9 12h6M9 16h4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>',
  story:
    '<path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H11v17H6.5A2.5 2.5 0 0 0 4 22V5.5ZM20 5.5A2.5 2.5 0 0 0 17.5 3H13v17h4.5A2.5 2.5 0 0 1 20 22V5.5Z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/>',
  check:
    '<path d="m5 12 4 4L19 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
  plus:
    '<path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>',
  minus:
    '<path d="M5 12h14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>',
  external:
    '<path d="M14 4h6v6M20 4l-9 9" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M18 13v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>',
  close:
    '<path d="M6 6l12 12M18 6 6 18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>',
  menu:
    '<path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>',
  install:
    '<path d="M12 3v12M7 10l5 5 5-5M5 21h14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>',
  shield:
    '<path d="M12 3 4.5 6v5.5c0 4.8 3.2 8 7.5 9.5 4.3-1.5 7.5-4.7 7.5-9.5V6L12 3Z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/><path d="m8.5 12 2.2 2.2 4.8-5" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>',
};

function icon(name, className = "") {
  return `<svg class="icon ${className}" viewBox="0 0 24 24" fill="none" aria-hidden="true">${iconPaths[name] || ""}</svg>`;
}

const routeKey = "rotabodrum-demo-route";
const missionKey = "rotabodrum-photo-missions";
let selectedRoute = new Set(
  JSON.parse(localStorage.getItem(routeKey) || "[]").filter((id) =>
    stops.some((stop) => stop.id === id),
  ),
);
let completedMissions = new Set(
  JSON.parse(localStorage.getItem(missionKey) || "[]").filter((id) =>
    missions.some((mission) => mission.stopId === id),
  ),
);
let activeFilter = "all";
let activeMissionId = null;
let missionPreviewUrl = null;
let deferredInstallPrompt = null;

const mapBounds = {
  minLat: Math.min(...stops.map((stop) => stop.lat)) - 0.008,
  maxLat: Math.max(...stops.map((stop) => stop.lat)) + 0.008,
  minLng: Math.min(...stops.map((stop) => stop.lng)) - 0.012,
  maxLng: Math.max(...stops.map((stop) => stop.lng)) + 0.012,
};

function stopPosition(stop) {
  const x =
    8 +
    ((stop.lng - mapBounds.minLng) / (mapBounds.maxLng - mapBounds.minLng)) * 84;
  const y =
    92 -
    ((stop.lat - mapBounds.minLat) / (mapBounds.maxLat - mapBounds.minLat)) * 84;
  const duplicateOffset = (stop.id % 3) * 0.8;
  return {
    x: Math.max(7, Math.min(93, x + duplicateOffset)),
    y: Math.max(7, Math.min(93, y - duplicateOffset)),
  };
}

function visibleStops() {
  if (activeFilter === "all") return stops;
  return stops.filter((stop) => stop.category === activeFilter);
}

function renderRecipes() {
  const rail = document.querySelector("#recipeRail");
  rail.innerHTML = recipes
    .map(
      (recipe) => `
        <button class="recipe-card recipe-card--${recipe.tone}" data-recipe-id="${recipe.id}" aria-label="${recipe.name} tarifini aç">
          <span class="recipe-card__index">${recipe.index}</span>
          <span class="recipe-card__art" aria-hidden="true">
            <span class="recipe-orbit recipe-orbit--one"></span>
            <span class="recipe-orbit recipe-orbit--two"></span>
            <span class="recipe-fruit"></span>
          </span>
          <span class="recipe-card__content">
            <span class="eyebrow">${recipe.category}</span>
            <strong>${recipe.name}</strong>
            <span>${recipe.summary}</span>
          </span>
          <span class="recipe-card__meta">
            <span>${recipe.time}</span>
            <span>${icon("arrow")}</span>
          </span>
        </button>
      `,
    )
    .join("");

  rail.querySelectorAll("[data-recipe-id]").forEach((card) => {
    card.addEventListener("click", () => openRecipe(card.dataset.recipeId));
  });
}

function renderMissions() {
  const rail = document.querySelector("#missionRail");
  rail.innerHTML = missions
    .map((mission, index) => {
      const stop = stops.find((item) => item.id === mission.stopId);
      const completed = completedMissions.has(mission.stopId);
      return `
        <button
          class="mission-card mission-card--${mission.theme} ${completed ? "is-complete" : ""}"
          data-mission-id="${mission.stopId}"
          aria-label="${mission.title} görevini aç"
        >
          <span class="mission-card__top">
            <span>CHECKPOINT ${String(index + 1).padStart(2, "0")}</span>
            <i>${completed ? icon("check") : String(stop.id).padStart(2, "0")}</i>
          </span>
          <span class="mission-card__visual" aria-hidden="true">
            <span></span><i></i><b></b>
          </span>
          <span class="mission-card__copy">
            <small>${stop.region} · ${stop.name}</small>
            <strong>${mission.title}</strong>
            <span>${completed ? "Görev tamamlandı" : "Fotoğraf görevini aç"} ${icon("arrow")}</span>
          </span>
        </button>
      `;
    })
    .join("");

  rail.querySelectorAll("[data-mission-id]").forEach((card) => {
    card.addEventListener("click", () => openMission(Number(card.dataset.missionId)));
  });

  updateMissionUI();
}

function updateMissionUI() {
  const count = completedMissions.size;
  const total = missions.length;
  const progress = total ? (count / total) * 100 : 0;
  document.querySelector("#missionSummaryCount").textContent = `${count}`;
  document.querySelector("#passportMissionCount").textContent = `${count} görev`;
  document.documentElement.style.setProperty("--mission-progress", `${progress}%`);
}

function renderMap() {
  const markerLayer = document.querySelector("#markerLayer");
  const list = document.querySelector("#routeList");
  const filtered = visibleStops();

  markerLayer.innerHTML = filtered
    .map((stop) => {
      const position = stopPosition(stop);
      const selected = selectedRoute.has(stop.id);
      const missionComplete = completedMissions.has(stop.id);
      return `
        <button
          class="map-marker ${selected ? "is-selected" : ""} ${missionComplete ? "is-mission-complete" : ""}"
          style="--marker-x:${position.x}%;--marker-y:${position.y}%"
          data-stop-id="${stop.id}"
          aria-label="${stop.name} detayını aç"
        >
          <span>${String(stop.id).padStart(2, "0")}</span>
        </button>
      `;
    })
    .join("");

  list.innerHTML = filtered
    .map(
      (stop) => `
        <button class="route-list-card ${selectedRoute.has(stop.id) ? "is-selected" : ""} ${completedMissions.has(stop.id) ? "is-mission-complete" : ""}" data-stop-id="${stop.id}">
          <span class="route-list-card__number">${String(stop.id).padStart(2, "0")}</span>
          <span class="route-list-card__content">
            <strong>${stop.name}</strong>
            <small>${stop.region} · ${stop.type}</small>
            ${completedMissions.has(stop.id) ? "<em>Görev tamamlandı</em>" : ""}
          </span>
          <span class="route-list-card__action">${icon(selectedRoute.has(stop.id) ? "check" : "arrow")}</span>
        </button>
      `,
    )
    .join("");

  document.querySelectorAll("[data-stop-id]").forEach((button) => {
    button.addEventListener("click", () => openStop(Number(button.dataset.stopId)));
  });

  updateRoutePath();
}

function updateRoutePath() {
  const selectedStops = stops.filter((stop) => selectedRoute.has(stop.id));
  const routePath = document.querySelector("#routePath");
  const routeGlow = document.querySelector("#routeGlow");

  if (selectedStops.length < 2) {
    const demoStops = [stops[9], stops[1], stops[7], stops[14], stops[6]];
    const demoPath = demoStops
      .map((stop, index) => {
        const position = stopPosition(stop);
        return `${index === 0 ? "M" : "L"} ${position.x} ${position.y}`;
      })
      .join(" ");
    routePath.setAttribute("d", demoPath);
    routeGlow.setAttribute("d", demoPath);
    routePath.classList.add("is-demo");
  } else {
    const selectedPath = selectedStops
      .map((stop, index) => {
        const position = stopPosition(stop);
        return `${index === 0 ? "M" : "L"} ${position.x} ${position.y}`;
      })
      .join(" ");
    routePath.setAttribute("d", selectedPath);
    routeGlow.setAttribute("d", selectedPath);
    routePath.classList.remove("is-demo");
  }

  updateRouteUI();
}

function updateRouteUI() {
  const routeStops = stops.filter((stop) => selectedRoute.has(stop.id));
  const count = routeStops.length;
  document.querySelectorAll("[data-route-count]").forEach((element) => {
    element.textContent = String(count);
    element.hidden = count === 0;
  });

  const tray = document.querySelector("#routeTray");
  const routeNames = document.querySelector("#routeNames");
  tray.classList.toggle("is-active", count > 0);
  routeNames.textContent =
    count > 0
      ? routeStops
          .slice(0, 3)
          .map((stop) => stop.region)
          .join(" → ") + (count > 3 ? ` +${count - 3}` : "")
      : "İlk durağını seç";

  const progress = Math.min(count / 5, 1);
  document.documentElement.style.setProperty("--passport-progress", `${progress * 100}%`);
  document.querySelector("#passportCount").textContent = `${count}/5`;
  document.querySelector("#passportStatus").textContent =
    count >= 5 ? "Rota Ustası" : count >= 3 ? "Kâşif" : count >= 1 ? "İz Sürücü" : "Başlangıç";

  document.querySelectorAll(".passport-stamp").forEach((stamp, index) => {
    stamp.classList.toggle("is-active", index < count);
  });
}

function persistRoute() {
  localStorage.setItem(routeKey, JSON.stringify([...selectedRoute]));
}

function toggleStop(stopId) {
  if (selectedRoute.has(stopId)) {
    selectedRoute.delete(stopId);
  } else {
    selectedRoute.add(stopId);
  }
  persistRoute();
  renderMap();
  if (document.querySelector("#stopSheet").open) {
    fillStopDialog(stops.find((stop) => stop.id === stopId));
  }
}

function fillStopDialog(stop) {
  const selected = selectedRoute.has(stop.id);
  const mission = missions.find((item) => item.stopId === stop.id);
  const missionComplete = completedMissions.has(stop.id);
  document.querySelector("#stopSheetIndex").textContent = String(stop.id).padStart(2, "0");
  document.querySelector("#stopSheetTitle").textContent = stop.name;
  document.querySelector("#stopSheetMeta").textContent = `${stop.region} · ${stop.type}`;
  document.querySelector("#stopSheetSignature").textContent = stop.signature;
  document.querySelector("#stopSheetDescription").textContent = stop.description;
  document.querySelector("#stopSheetStatus").textContent = stop.verification;
  document.querySelector("#stopSheetMap").href = stop.mapLink;

  const missionButton = document.querySelector("#stopMissionButton");
  missionButton.dataset.missionId = String(stop.id);
  missionButton.classList.toggle("is-complete", missionComplete);
  document.querySelector("#stopMissionIndex").textContent =
    `CHECKPOINT ${String(stop.id).padStart(2, "0")}`;
  document.querySelector("#stopMissionTitle").textContent = mission.title;
  document.querySelector("#stopMissionState").textContent = missionComplete
    ? "Tamamlandı · görevi aç"
    : "Fotoğraf görevini aç";

  const toggleButton = document.querySelector("#stopSheetToggle");
  toggleButton.dataset.stopId = String(stop.id);
  toggleButton.classList.toggle("is-selected", selected);
  toggleButton.innerHTML = `${icon(selected ? "minus" : "plus")} ${
    selected ? "Rotadan çıkar" : "Rotama ekle"
  }`;
}

function openStop(stopId) {
  const stop = stops.find((item) => item.id === stopId);
  if (!stop) return;
  fillStopDialog(stop);
  document.querySelector("#stopSheet").showModal();
}

function clearMissionPhoto() {
  if (missionPreviewUrl) {
    URL.revokeObjectURL(missionPreviewUrl);
    missionPreviewUrl = null;
  }
  const input = document.querySelector("#missionPhotoInput");
  input.value = "";
  document.querySelector("#missionPhotoImage").removeAttribute("src");
  document.querySelector("#missionPhotoPreview").hidden = true;
  document.querySelector("#completeMissionButton").disabled = true;
}

function openMission(stopId) {
  const mission = missions.find((item) => item.stopId === stopId);
  const stop = stops.find((item) => item.id === stopId);
  if (!mission || !stop) return;

  activeMissionId = stopId;
  clearMissionPhoto();
  document.querySelector("#missionSheetIndex").textContent =
    `CHECKPOINT ${String(stop.id).padStart(2, "0")}`;
  document.querySelector("#missionSheetRegion").textContent = stop.region;
  document.querySelector("#missionSheetStop").textContent = stop.name;
  document.querySelector("#missionSheetTitle").textContent = mission.title;
  document.querySelector("#missionSheetPrompt").textContent = mission.prompt;
  document.querySelector("#missionSheetClue").textContent = mission.clue;
  document.querySelector("#missionMapLink").href = stop.mapLink;

  const completeButton = document.querySelector("#completeMissionButton");
  completeButton.textContent = completedMissions.has(stopId)
    ? "Yeni fotoğrafla güncelle"
    : "Görevi tamamla";
  document.querySelector("#missionSheet").showModal();
}

function handleMissionPhoto(event) {
  const [file] = event.target.files;
  if (!file || !file.type.startsWith("image/")) {
    clearMissionPhoto();
    return;
  }

  if (missionPreviewUrl) URL.revokeObjectURL(missionPreviewUrl);
  missionPreviewUrl = URL.createObjectURL(file);
  document.querySelector("#missionPhotoImage").src = missionPreviewUrl;
  document.querySelector("#missionPhotoPreview").hidden = false;
  document.querySelector("#completeMissionButton").disabled = false;
}

function completeMission() {
  const [file] = document.querySelector("#missionPhotoInput").files;
  if (!activeMissionId || !file) return;

  completedMissions.add(activeMissionId);
  localStorage.setItem(missionKey, JSON.stringify([...completedMissions]));
  const completedMission = missions.find((mission) => mission.stopId === activeMissionId);
  clearMissionPhoto();
  document.querySelector("#missionSheet").close();
  renderMissions();
  renderMap();
  showToast(`“${completedMission.title}” görevi pasaportuna işlendi.`);
}

let toastTimer;
function showToast(message) {
  const toast = document.querySelector("#appToast");
  toast.textContent = message;
  toast.classList.add("is-visible");
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => toast.classList.remove("is-visible"), 3200);
}

function openRecipe(recipeId) {
  const recipe = recipes.find((item) => item.id === recipeId);
  if (!recipe) return;

  document.querySelector("#recipeSheetCategory").textContent = recipe.category;
  document.querySelector("#recipeSheetTitle").textContent = recipe.name;
  document.querySelector("#recipeSheetSummary").textContent = recipe.summary;
  document.querySelector("#recipeSheetTime").textContent = recipe.time;
  document.querySelector("#recipeSheetSeason").textContent = recipe.season;
  document.querySelector("#recipeSheetIngredients").innerHTML = recipe.ingredients
    .map((ingredient) => `<li>${icon("check")}<span>${ingredient}</span></li>`)
    .join("");
  document.querySelector("#recipeSheetArt").className = `sheet-art recipe-card--${recipe.tone}`;
  document.querySelector("#recipeSheet").showModal();
}

function closeDialogOnBackdrop(dialog) {
  dialog.addEventListener("click", (event) => {
    const bounds = dialog.getBoundingClientRect();
    const inside =
      event.clientX >= bounds.left &&
      event.clientX <= bounds.right &&
      event.clientY >= bounds.top &&
      event.clientY <= bounds.bottom;
    if (!inside) dialog.close();
  });
}

function setupControls() {
  document.querySelectorAll("[data-filter]").forEach((button) => {
    button.addEventListener("click", () => {
      activeFilter = button.dataset.filter;
      document.querySelectorAll("[data-filter]").forEach((filter) => {
        filter.classList.toggle("is-active", filter === button);
      });
      renderMap();
    });
  });

  document.querySelector("#stopSheetToggle").addEventListener("click", (event) => {
    toggleStop(Number(event.currentTarget.dataset.stopId));
  });
  document.querySelector("#stopMissionButton").addEventListener("click", (event) => {
    const missionId = Number(event.currentTarget.dataset.missionId);
    document.querySelector("#stopSheet").close();
    requestAnimationFrame(() => openMission(missionId));
  });
  document.querySelector("#missionPhotoInput").addEventListener("change", handleMissionPhoto);
  document.querySelector("#missionPhotoRemove").addEventListener("click", () => {
    clearMissionPhoto();
    document.querySelector("#missionPhotoInput").click();
  });
  document.querySelector("#completeMissionButton").addEventListener("click", completeMission);
  document.querySelector("#missionSheet").addEventListener("close", clearMissionPhoto);
  document.querySelector("#clearRoute").addEventListener("click", () => {
    selectedRoute.clear();
    persistRoute();
    renderMap();
  });
  document.querySelector("#openRouteButton").addEventListener("click", () => {
    document.querySelector("#route").scrollIntoView({ behavior: "smooth" });
  });

  document.querySelectorAll("[data-dialog-close]").forEach((button) => {
    button.addEventListener("click", () => button.closest("dialog").close());
  });
  document.querySelectorAll("dialog").forEach(closeDialogOnBackdrop);

  const navToggle = document.querySelector("#navToggle");
  const navPanel = document.querySelector("#navPanel");
  navToggle.addEventListener("click", () => {
    const open = navPanel.classList.toggle("is-open");
    navToggle.setAttribute("aria-expanded", String(open));
    navToggle.innerHTML = icon(open ? "close" : "menu");
  });
  navPanel.querySelectorAll("a").forEach((link) => {
    link.addEventListener("click", () => {
      navPanel.classList.remove("is-open");
      navToggle.setAttribute("aria-expanded", "false");
      navToggle.innerHTML = icon("menu");
    });
  });

}

function setupInstall() {
  const installButton = document.querySelector("#installButton");
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredInstallPrompt = event;
    installButton.hidden = false;
  });

  installButton.addEventListener("click", async () => {
    if (!deferredInstallPrompt) return;
    deferredInstallPrompt.prompt();
    await deferredInstallPrompt.userChoice;
    deferredInstallPrompt = null;
    installButton.hidden = true;
  });
}

function setupMotion(sceneExperience) {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  gsap.utils.toArray(".reveal").forEach((element) => {
    gsap.fromTo(
      element,
      { y: 48, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: element,
          start: "top 84%",
          once: true,
        },
      },
    );
  });

  gsap.utils.toArray(".stagger-group").forEach((group) => {
    gsap.fromTo(
      group.children,
      { y: 28, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        stagger: 0.08,
        duration: 0.75,
        ease: "power2.out",
        scrollTrigger: {
          trigger: group,
          start: "top 80%",
          once: true,
        },
      },
    );
  });

  const header = document.querySelector("#appHeader");
  ScrollTrigger.create({
    start: 80,
    onUpdate: (self) => header.classList.toggle("is-scrolled", self.scroll() > 80),
  });

  requestAnimationFrame(() => {
    sceneExperience.refresh();
    ScrollTrigger.refresh();
  });
}

function setupSectionNavigation() {
  const sections = [...document.querySelectorAll("main section[id]")];
  const navLinks = [...document.querySelectorAll("[data-nav-target]")];
  const observer = new IntersectionObserver(
    (entries) => {
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (!visible) return;
      navLinks.forEach((link) => {
        link.classList.toggle("is-active", link.dataset.navTarget === visible.target.id);
      });
    },
    { threshold: [0.2, 0.45, 0.65], rootMargin: "-15% 0px -50% 0px" },
  );
  sections.forEach((section) => observer.observe(section));
}

renderRecipes();
renderMissions();
renderMap();
setupControls();
setupInstall();
setupSectionNavigation();

document.querySelector("#currentYear").textContent = String(new Date().getFullYear());
setupMotion({ refresh() {} });

import("./mandarin-scene.js")
  .then(({ createMandarinExperience }) => {
    const sceneExperience = createMandarinExperience(document.querySelector("#mandarinCanvas"));
    requestAnimationFrame(() => sceneExperience.refresh());
  })
  .catch((error) => {
    console.warn("3D deneyim yüklenemedi", error);
    document.documentElement.classList.add("no-webgl");
  });

if ("serviceWorker" in navigator && import.meta.env.PROD) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("./sw.js").catch((error) => {
      console.info("Service worker devre dışı", error);
    });
  });
}
